import { create, v, destroy } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';
import { createNlsMiddleware, guidedtourChange, lccstate, landingChange } from 'lcc-state';
import { publish } from 'pub-sub';
import { XAPI } from '@holmescorp/xapi';
import videojs from 'video.js';
import 'video-hls-quality-selector';
import css from '../css/video.m.css';
const nls = createNlsMiddleware('video');
const factory = create({
    theme,
    nls,
    destroy,
    lccstate
}).properties();
export const Video = factory(({ properties, middleware: { theme, nls, destroy, lccstate } }) => {
    const { videonum, fromWelcomePage = false, videoOnly = false } = properties();
    const { isPlaceholder, messages } = nls();
    if (isPlaceholder) {
        return null;
    }
    const themedCss = theme.classes(css);
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const videoid = `video-${videonum}`;
    const titleText = messages[`title_${videonum}`] || '';
    const instructionText = messages[`instructions_${videonum}`] || '';
    const videoTranscriptText = messages[`transcript_text`] || '';
    const videoTranscript = messages[`transcript_${videonum}`] || 'false';
    const videoTranscriptPath = messages[`transcript_path_${videonum}`] || '';
    const transcriptArray = [];
    if (videoTranscript === 'true') {
        transcriptArray.push(v('a', { href: videoTranscriptPath, target: '_blank' }, [`${videoTranscriptText} ${titleText}`]));
    }
    const setup_videojs = () => {
        const lang = window.hcstatic.portal_language || 'en';
        const filePath = messages[`path_${videonum}`] || '';
        const videoPlayer = videojs(videoid, {
            autoplay: false,
            controls: true,
            language: lang,
            poster: `${filePath}.png`,
            preload: 'auto'
        });
        const { association, product } = window.hcstatic;
        const assoc = association?.toLowerCase();
        const actor = lccstate.get(lccstate.path('actor_xapi'));
        const target = new XAPI.Activity({ id: 'https://w3id.org/xapi/video/activity-type/video' });
        const context = new XAPI.Context({
            extensions: { 'urn:holmes:video-id': `${assoc}-${product}-${videonum}` }
        });
        const _xapi_statement = (verb) => {
            return new XAPI.Statement({
                actor,
                verb,
                target,
                context
            }).asVersion();
        };
        videoPlayer.on('play', () => {
            const verb = XAPI.Verbs.PLAYED;
            XAPI.svc.set_statement({ stmnt: _xapi_statement(verb) });
        });
        videoPlayer.on('pause', () => {
            const verb = XAPI.Verbs.PAUSED;
            XAPI.svc.set_statement({ stmnt: _xapi_statement(verb) });
        });
        videoPlayer.on('seek', () => {
            const verb = XAPI.Verbs.SEEKED;
            XAPI.svc.set_statement({ stmnt: _xapi_statement(verb) });
        });
        videoPlayer.on('ended', () => {
            const verb = XAPI.Verbs.COMPLETED;
            XAPI.svc.set_statement({ stmnt: _xapi_statement(verb) });
        });
        const videoHd = messages[`hd_${videonum}`] || 'false';
        const videoAdaptive = messages[`adaptive_${videonum}`] || 'false';
        const videoSources = [];
        if (videoAdaptive === 'true') {
            videoSources.push({ type: 'application/x-mpegURL', src: `${filePath}/playlist.m3u8` });
        }
        videoSources.push({ type: 'video/mp4', src: `${filePath}.mp4` });
        if (videoHd === 'true') {
            videoSources.push({ type: 'video/mp4', src: `${filePath}_hd.mp4` });
        }
        videoPlayer.src(videoSources);
        videoPlayer.hlsQualitySelector({ displayCurrentQuality: true });
        const videoVtt = messages[`vtt_${videonum}`] || 'false';
        if (videoVtt === 'true') {
            videoPlayer.addRemoteTextTrack({ kind: 'captions', src: `${filePath}.vtt`, language: lang, label: lang }, true);
        }
        videoPlayer.ready(() => {
            const playButton = videoPlayer.el().querySelector('.vjs-big-play-button');
            if (playButton) {
                playButton.setAttribute('title', `Play ${titleText} video`);
                const controlText = playButton.querySelector('.vjs-control-text');
                if (controlText) {
                    controlText.textContent = `Play ${titleText} video`;
                }
            }
        });
        if (fromWelcomePage) {
            if (`${videonum}` === '1') {
                videoPlayer.on('ended', async () => {
                    const verb = XAPI.Verbs.COMPLETED;
                    XAPI.svc.set_statement({ stmnt: _xapi_statement(verb) });
                    landingChange({ watched_video: true });
                    await delay(5000);
                    if (videoOnly) {
                        landingChange({ complete: true });
                        publish('active-view', 'assess');
                    }
                    else {
                        publish('site/landingpage');
                    }
                });
            }
        }
        else {
            if (`${videonum}` === '1') {
                videoPlayer.on('ended', async () => {
                    const verb = XAPI.Verbs.COMPLETED;
                    XAPI.svc.set_statement({ stmnt: _xapi_statement(verb) });
                    guidedtourChange({ guidedtour: true });
                    await delay(5000);
                    publish('site/homepage');
                });
            }
        }
    };
    publish('state/viewtitle', { viewtitle: titleText });
    destroy(() => {
        videojs(videoid).dispose();
    });
    delay(100).then(() => {
        setup_videojs();
    });
    return v('div', { classes: [themedCss.root] }, [
        v('div', { classes: [themedCss.videoInstructions] }, [instructionText]),
        v('div', { classes: [themedCss.videoWrapper], 'data-vjs-player': '' }, [
            v('video-js', { id: videoid, classes: ['video-js', 'vjs-big-play-centered'] })
        ]),
        v('div', { classes: [themedCss.videoTranscript] }, transcriptArray)
    ]);
});
